import { Map } from "immutable";
import * as _ from "lodash";

import { SessionStore } from "shared/src/uidispatcherapi/SessionStore";

import * as TrackingUtil from "shared/src/utils/TrackingUtil";
import { Store } from "shared/src/stores/Store";
import * as ArticleSearchClient from "../clients/ArticleSearchClient";
import * as CategoryClient from "../clients/CategoryClient";
import type { CategorySettings } from "../clients/CategoryClient";
import MainCategoryAction from "../actions/MainCategoryAction";
import { determineCategoryTreeProfile } from "../utils/determineSearchProfile";
import { isFsdShop } from "shared/src/utils/ShopFlavor";
import { getContextDependentAdditionalFilters } from "../utils/SearchUtil";
import type { CategoryRoot } from "shared/src/types/articlesearch/Category";
import { Category } from "shared/src/types/articlesearch/Category";

class MainCategoryStore extends Store {
    mainCategoriesAlreadyLoading: boolean;
    categoriesSettingsAlreadyLoading: boolean;

    country: string | undefined | null;
    customerStore: string | undefined | null;
    customerId: string | undefined | null;

    _result: {
        categories: CategoryRoot | undefined | null;
        categoriesSettings: CategorySettings | undefined | null;
    };
    _categoryIndex: Map<string, any>;

    constructor() {
        super();
        this._result = this.getInitialState();
        this._categoryIndex = Map();

        this.mainCategoriesAlreadyLoading = false;
        this.categoriesSettingsAlreadyLoading = false;

        this.country = null;
        this.customerStore = null;
        this.customerId = null;

        this.listenTo(MainCategoryAction.loadMainCategories, this.loadMainCategories);
        this.listenTo(SessionStore, this.onSessionStoreTrigger);
        this.onSessionStoreTrigger();
    }

    getInitialState() {
        return {
            categories: undefined,
            categoriesSettings: undefined
        };
    }

    onSessionStoreTrigger() {
        // only reload the category data if relevant stuff Changed in the SessionStore
        if (
            (this.country === SessionStore.getCountry() &&
                this.customerStore === SessionStore.getCustomerStore() &&
                this.customerId === SessionStore.getCustomerId()) ||
            SessionStore.pending()
        ) {
            return;
        }

        this.country = SessionStore.getCountry();
        this.customerStore = SessionStore.getCustomerStore();
        this.customerId = SessionStore.getCustomerId();

        this._result.categories = undefined;
        this._categoryIndex = Map();
        this.loadMainCategories();

        this._result.categoriesSettings = undefined;
        this.loadCategoriesSettings();
    }

    loadCategoriesSettings() {
        if (this._result.categoriesSettings) {
            this.trigger(this._result);
            return;
        }

        if (!SessionStore.getCountry()) {
            return;
        }

        if (!this.categoriesSettingsAlreadyLoading) {
            this.categoriesSettingsAlreadyLoading = true;
            CategoryClient.getSettings(SessionStore.getCountry())
                .then((result) => this.setCategoriesSettings(result))
                .catch((e) => this.getCategoriesSettingsFailed(e));
        }
    }

    loadMainCategories() {
        if (this._result.categories) {
            this.trigger(this._result);
            return;
        }
        if (!SessionStore.getCustomerStore()) {
            return;
        }
        if (!this.mainCategoriesAlreadyLoading) {
            this.mainCategoriesAlreadyLoading = true;
            if (isFsdShop()) {
                // We want the tree only for FSD, and the mainCategories endpoint doesn't support that. But we can just
                // use the search for this anyway
                // TODO(FD) we could just use this everywhere, but I want to test this a bit
                // Maybe I'm forgetting some reason why the mainCategories endpoint exists
                const filters = getContextDependentAdditionalFilters();
                const request = {
                    storeId: SessionStore.getCustomerStore(),
                    customerId: SessionStore.getCustomerId(),
                    language: SessionStore.getLocale(),
                    country: SessionStore.getCountry(),
                    profile: determineCategoryTreeProfile(),
                    categories: true,
                    facets: false,
                    filter: filters,
                    query: "*",
                    rows: 0,
                    page: 1
                };
                ArticleSearchClient.doSearch(request)
                    .then((result) => this.setCategories(result.categorytree!))
                    .catch((e) => this.getCategoriesFailed(e));
            } else {
                const requestQuery = {
                    storeId: SessionStore.getCustomerStore(),
                    customerId: SessionStore.getCustomerId(),
                    language: SessionStore.getLocale(),
                    country: SessionStore.getCountry(),
                    profile: determineCategoryTreeProfile()
                };
                ArticleSearchClient.getMainCategories(requestQuery)
                    .then((result) => this.setCategories(result))
                    .catch((e) => this.getCategoriesFailed(e));
            }
        }
    }

    getCategoryPathForId(id: string) {
        return this.getForId(id, (x) => x.categoryPath, []);
    }

    getCategoryForId(id: string) {
        return this.getForId(id, (x) => x.category, undefined);
    }

    getForId<T>(id: string, accessor: (x0: { [x: string]: any }) => T, def: T): T {
        if (this._categoryIndex.get(id) && accessor(this._categoryIndex.get(id))) {
            return accessor(this._categoryIndex.get(id));
        }

        for (const part of Array.from(this._categoryIndex.keys() as any)
            .sort()
            .reverse() as any[]) {
            if (_.startsWith(id, part)) {
                const found = this._categoryIndex.get(part) && accessor(this._categoryIndex.get(part));
                if (found) {
                    return found;
                }
            }
        }
        return def;
    }

    hasResults() {
        return this._result.categories && !this.mainCategoriesAlreadyLoading;
    }

    getCategories() {
        return this._result;
    }

    getOrderedMainCategories(): Category[] {
        const result = [];
        if (this._result.categories) {
            const categoriesByName = _.keyBy(this._result.categories.children, (category) => category.name);
            for (const name of this._result.categories.order) {
                result.push(categoriesByName[name]);
            }
        }
        return result;
    }

    indexCategoryRecursively(
        cat: {
            [x: string]: any;
        },
        categoryPath: Array<{
            [x: string]: any;
        }> = []
    ): Map<string, any> {
        // build an index over category_id (url_category_path or node_id to category) over all children
        // also add to the leaf nodes a list of all categories on the path to the leaf
        let result = Map<string, any>().withMutations((mutableMap: any) => {
            _.forEach(cat.children || {}, (childCat) => {
                mutableMap.merge(this.indexCategoryRecursively(childCat, [...categoryPath, childCat]));
            });
        });

        if (cat.urlCategoryPath) {
            result = result.set(cat.urlCategoryPath, {
                category: cat,
                categoryPath: categoryPath
            });
        }
        if (cat.nodeId) {
            result = result.set(cat.nodeId, {
                category: cat,
                categoryPath: categoryPath
            });
        }

        return result;
    }

    setCategories(result: CategoryRoot) {
        this.mainCategoriesAlreadyLoading = false;
        this._result.categories = result;
        this._categoryIndex = this.indexCategoryRecursively(result);
        this.trigger(this._result);
    }

    getCategoriesFailed(err: any) {
        this.mainCategoriesAlreadyLoading = false;
        this._result.categories = undefined;
        this._categoryIndex = Map();
        TrackingUtil.trackUiErrorSd("MainCategoryStore", "getCategories failed.", err);
        // retry in 1 minute
        setTimeout(() => this.loadMainCategories(), 1000 * 60);
    }

    setCategoriesSettings(result: CategorySettings) {
        this.categoriesSettingsAlreadyLoading = false;
        this._result.categoriesSettings = result;
        this.trigger(this._result);
    }

    getCategoriesSettingsFailed(err: any) {
        this.categoriesSettingsAlreadyLoading = false;
        this._result.categoriesSettings = undefined;
        TrackingUtil.trackUiErrorSd("MainCategoryStore", "getCategoriesSettings failed.", err);
        // retry in 1 minute
        setTimeout(() => this.loadCategoriesSettings(), 1000 * 60);
    }
}

export const MainCategoryStoreClass = MainCategoryStore;

export default new MainCategoryStore();
